import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShieldAlt } from "@awesome.me/kit-269fbef2e2/icons/duotone/solid";
import companyService from "@/services/companyService";
import { copyControlsToCustomerSOA } from "@/services/frameworkService";
import { toast } from "sonner";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue
} from "@/components/ui/select";
import { DatePicker } from "@/components/ui/date-picker";
import { cleanDateAndTimeObject, toLocalDateString } from "../../../../../../shared/renderDate";

export default function ISO27001Module({
  tabValue = "iso27001",
}) {
  // State
  const [iso27001, setIso27001] = useState({
    auditFirm: "",
    primaryContact: "",
    auditorName: "",
    stage1Date: undefined,
    stage1Result: "",
    stage2Date: undefined,
    stage2Result: "",
    lastAuditDate: undefined,
    nextAuditDate: undefined,
    enabled: "disabled",
  });
  const [iso27001Initial, setIso27001Initial] = useState(null);
  const [isLoadingISO, setIsLoadingISO] = useState(true);
  const [isSavingISO, setIsSavingISO] = useState(false);

  // Utility: Firestore array -> state
  function isoArrayToState(arr, active) {
    const get = (name) => arr?.find(e => e.settingName === name)?.value || "";
    // Convert date strings to Date objects
    const parseDate = (dateValue) => {
      if (!dateValue) return undefined;
      // Handle YYYY-MM-DD format directly
      if (typeof dateValue === "string" && dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = dateValue.split("-");
        return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      }
      const date = cleanDateAndTimeObject(dateValue);
      return date;
    };
    return {
      auditFirm: get("ISO Audit Firm"),
      primaryContact: get("Primary Contact"),
      auditorName: get("Auditor Name"),
      stage1Date: parseDate(get("Stage 1 Date")),
      stage1Result: get("Stage 1 Result"),
      stage2Date: parseDate(get("Stage 2 Date")),
      stage2Result: get("Stage 2 Result"),
      lastAuditDate: parseDate(get("Last Audit Date")),
      nextAuditDate: parseDate(get("Next Audit Date")),
      enabled: active ? "enabled" : "disabled",
    };
  }
  // Utility: state -> Firestore array
  function isoStateToArray(state) {
    // Convert Date objects to date strings for Firestore
    const formatDateForFirestore = (date) => {
      if (!date) return "";
      if (date instanceof Date) {
        // Format as YYYY-MM-DD without timezone conversion
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      }
      return date;
    };
    
    return [
      { settingName: "ISO Audit Firm", value: state.auditFirm },
      { settingName: "Primary Contact", value: state.primaryContact },
      { settingName: "Auditor Name", value: state.auditorName },
      { settingName: "Stage 1 Date", value: formatDateForFirestore(state.stage1Date) },
      { settingName: "Stage 1 Result", value: state.stage1Result },
      { settingName: "Stage 2 Date", value: formatDateForFirestore(state.stage2Date) },
      { settingName: "Stage 2 Result", value: state.stage2Result },
      { settingName: "Last Audit Date", value: formatDateForFirestore(state.lastAuditDate) },
      { settingName: "Next Audit Date", value: formatDateForFirestore(state.nextAuditDate) },
    ];
  }

  // Load ISO 27001 settings on mount
  useEffect(() => {
    const fetchISO = async () => {
      setIsLoadingISO(true);
      try {
        const settings = await companyService.getCompanySettings(undefined, "complianceModules");
        const isoArr = settings?.["iso27001-2013"] || [];
        const isoActive = settings?.["iso27001-2013"]?.active ?? false;
        const arr = Array.isArray(isoArr) ? isoArr : Object.values(isoArr).filter(x => typeof x === 'object' && x.settingName);
        const state = isoArrayToState(arr, isoActive);
        setIso27001(state);
        setIso27001Initial(state);
      } catch (err) {
        console.error("Failed to fetch ISO 27001 settings:", err);
        toast.error("Failed to load ISO 27001 settings");
      } finally {
        setIsLoadingISO(false);
      }
    };
    fetchISO();
  }, []);

  // Change detection
  const isoChanged = (() => {
    if (!iso27001Initial) return false;
    
    // Compare all fields except dates
    const basicFieldsChanged = (
      iso27001.auditFirm !== iso27001Initial.auditFirm ||
      iso27001.primaryContact !== iso27001Initial.primaryContact ||
      iso27001.auditorName !== iso27001Initial.auditorName ||
      iso27001.stage1Result !== iso27001Initial.stage1Result ||
      iso27001.stage2Result !== iso27001Initial.stage2Result ||
      iso27001.enabled !== iso27001Initial.enabled
    );
    
    if (basicFieldsChanged) return true;
    
    // Compare dates separately
    const dateFields = ['stage1Date', 'stage2Date', 'lastAuditDate', 'nextAuditDate'];
    return dateFields.some(field => {
      const date1 = iso27001[field];
      const date2 = iso27001Initial[field];
      
      // If both are undefined or null, they're equal
      if (!date1 && !date2) return false;
      
      // If one is undefined/null and the other isn't, they're different
      if (!date1 || !date2) return true;
      
      // If both are Date objects, compare their time values
      return date1.getTime() !== date2.getTime();
    });
  })();

  // Save handler
  const handleSaveISO = async () => {
    setIsSavingISO(true);
    try {
      const wasDisabled = iso27001Initial?.enabled === "disabled";
      const isNowEnabled = iso27001.enabled === "enabled";
      
      const isoArr = isoStateToArray(iso27001);
      const isoActive = isNowEnabled;
      
      await companyService.saveCompanySettings(undefined, "complianceModules", {
        "iso27001-2013": {
          ...(isoArr.reduce((acc, item, idx) => ({ ...acc, [idx]: item }), {})),
          active: isoActive,
        }
      });
      
      // If ISO 27001 is being enabled (was disabled, now enabled), copy controls
      if (wasDisabled && isNowEnabled) {
        try {
          const customerId = window.companyID;
          
          if (!customerId) {
            throw new Error('Company ID not found');
          }
          
          toast.info("Copying ISO 27001 controls...");
          
          await copyControlsToCustomerSOA(
            customerId,
            'ISO27001',
            undefined
          );
          
          toast.success("ISO 27001 settings saved and controls copied");
        } catch (controlError) {
          console.error('Error copying ISO 27001 controls:', controlError);
          toast.warning("Settings saved, but there was an error copying some controls. Please check the console.");
        }
      } else {
        toast.success("ISO 27001 settings saved");
      }
      
      setIso27001Initial({ ...iso27001 });
    } catch (err) {
      console.error("Failed to save ISO 27001 settings:", err);
      toast.error("Failed to save ISO 27001 settings");
    } finally {
      setIsSavingISO(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Module Status */}
      <div className="space-y-2">
        <label className="block text-sm font-medium mb-1">Module Enabled</label>
        <Select
          value={iso27001.enabled}
          onValueChange={val => setIso27001(v => ({ ...v, enabled: val }))}
          disabled={isLoadingISO || isSavingISO}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="enabled">Enabled</SelectItem>
            <SelectItem value="disabled">Disabled</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {/* ISO Audit Firm */}
      <div className="space-y-2">
        <label htmlFor="isoAuditFirmInput" className="block text-sm font-medium text-foreground">
          ISO Audit Firm
        </label>
        <input
          id="isoAuditFirmInput"
          type="text"
          className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
          value={iso27001.auditFirm}
          onChange={e => setIso27001(v => ({ ...v, auditFirm: e.target.value }))}
          disabled={isLoadingISO || isSavingISO}
          placeholder="Firm Name"
        />
        <p className="text-xs text-muted-foreground">
          The name of the firm that conducted your ISO audit.<br />For example, Pondstone Partners Pty Ltd.
        </p>
      </div>
      {/* Primary Contact */}
      <div className="space-y-2">
        <label htmlFor="primaryContactInput" className="block text-sm font-medium text-foreground">
          Primary Contact
        </label>
        <input
          id="primaryContactInput"
          type="text"
          className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
          value={iso27001.primaryContact}
          onChange={e => setIso27001(v => ({ ...v, primaryContact: e.target.value }))}
          disabled={isLoadingISO || isSavingISO}
          placeholder="Bob Bobbington"
        />
        <p className="text-xs text-muted-foreground">
          The main contact person for your ISO audit process.<br />For example, Harold Quackmore.
        </p>
      </div>
      {/* Auditor Name */}
      <div className="space-y-2">
        <label htmlFor="auditorNameInput" className="block text-sm font-medium text-foreground">
          Auditor Name
        </label>
        <input
          id="auditorNameInput"
          type="text"
          className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
          value={iso27001.auditorName}
          onChange={e => setIso27001(v => ({ ...v, auditorName: e.target.value }))}
          disabled={isLoadingISO || isSavingISO}
          placeholder="Harold Bishop"
        />
        <p className="text-xs text-muted-foreground">
          The name of the auditor who performed your ISO audit.<br />For example, Harold Quackmore.
        </p>
      </div>
      {/* Stage 1 Date */}
      <div className="space-y-2">
        <label htmlFor="stage1DateInput" className="block text-sm font-medium text-foreground">
          Stage 1 Date
        </label>
        <DatePicker
          date={iso27001.stage1Date}
          setDate={(date) => setIso27001(v => ({ ...v, stage1Date: date ? cleanDateAndTimeObject(date, true) : undefined }))}
          fullWidth
          enableNaturalInput
          showPreview
          placeholder="Pick a date"
        />
        <p className="text-xs text-muted-foreground">
          The date when the first stage of your ISO audit took place.<br />For example, 15/01/2023
        </p>
      </div>
      {/* Stage 1 Result */}
      <div className="space-y-2">
        <label htmlFor="stage1ResultInput" className="block text-sm font-medium text-foreground">
          Stage 1 Result
        </label>
        <input
          id="stage1ResultInput"
          type="text"
          className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
          value={iso27001.stage1Result}
          onChange={e => setIso27001(v => ({ ...v, stage1Result: e.target.value }))}
          disabled={isLoadingISO || isSavingISO}
          placeholder="Pass"
        />
        <p className="text-xs text-muted-foreground">
          The result of the first stage of your ISO audit.<br />For example, Pass
        </p>
      </div>
      {/* Stage 2 Date */}
      <div className="space-y-2">
        <label htmlFor="stage2DateInput" className="block text-sm font-medium text-foreground">
          Stage 2 Date
        </label>
        <DatePicker
          date={iso27001.stage2Date}
          setDate={(date) => setIso27001(v => ({ ...v, stage2Date: date ? cleanDateAndTimeObject(date, true) : undefined }))}
          fullWidth
          enableNaturalInput
          showPreview
          placeholder="Pick a date"
        />
        <p className="text-xs text-muted-foreground">
          The date when the second stage of your ISO audit took place.<br />For example, 10/03/2023
        </p>
      </div>
      {/* Stage 2 Result */}
      <div className="space-y-2">
        <label htmlFor="stage2ResultInput" className="block text-sm font-medium text-foreground">
          Stage 2 Result
        </label>
        <input
          id="stage2ResultInput"
          type="text"
          className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
          value={iso27001.stage2Result}
          onChange={e => setIso27001(v => ({ ...v, stage2Result: e.target.value }))}
          disabled={isLoadingISO || isSavingISO}
          placeholder="Pass"
        />
        <p className="text-xs text-muted-foreground">
          The result of the second stage of your ISO audit.<br />For example, Conditional Pass
        </p>
      </div>
      {/* Last Audit Date */}
      <div className="space-y-2">
        <label htmlFor="lastAuditDateInput" className="block text-sm font-medium text-foreground">
          Last Audit Date
        </label>
        <DatePicker
          date={iso27001.lastAuditDate}
          setDate={(date) => setIso27001(v => ({ ...v, lastAuditDate: date ? cleanDateAndTimeObject(date, true) : undefined }))}
          fullWidth
          enableNaturalInput
          showPreview
          placeholder="Pick a date"
        />
        <p className="text-xs text-muted-foreground">
          The date of the most recent audit before the current one.<br />For example, 05/12/2022
        </p>
      </div>
      {/* Next Audit Date */}
      <div className="space-y-2">
        <label htmlFor="nextAuditDateInput" className="block text-sm font-medium text-foreground">
          Next Audit Date
        </label>
        <DatePicker
          date={iso27001.nextAuditDate}
          setDate={(date) => setIso27001(v => ({ ...v, nextAuditDate: date ? cleanDateAndTimeObject(date, true) : undefined }))}
          fullWidth
          enableNaturalInput
          showPreview
          placeholder="Pick a date"
        />
        <p className="text-xs text-muted-foreground">
          The scheduled date for your next ISO audit.<br />For example, 20/01/2024
        </p>
      </div>
      <div className="pt-4">
        <button
          className="px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors flex items-center gap-2"
          onClick={handleSaveISO}
          disabled={isSavingISO || !isoChanged}
        >
          {isSavingISO && (
            <svg className="animate-spin h-4 w-4 mr-2 text-primary-foreground" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
            </svg>
          )}
          Save ISO 27001 Settings
        </button>
      </div>
    </div>
  );
}
